<script setup lang="ts">
import type { ColorVariant } from 'bootstrap-vue-next';
import { BBadge, BButton, BTable, BLink } from 'bootstrap-vue-next';
import { computed, onMounted, ref } from 'vue';

import type { Registration } from '~/api/transcendHackathon';
import {
  approveRegistration,
  fetchRegistrations,
  rejectRegistration,
} from '~/api/transcendHackathon';
import ErrorState from '~/common/components/ErrorState.vue';
import ThemedOverlay from '~/common/components/ThemedOverlay.vue';
import { dateFieldFormatter } from '~/common/FieldFormats';

const registrations = ref<Registration[]>([]);
const loading = ref(true);
const error = ref(false);
const busy = ref(new Map<number, Action>());

type Action = 'approve' | 'reject';

const statusVariant: Record<string, ColorVariant> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'secondary',
};

const fields = [
  { key: 'username', label: 'User' },
  { key: 'devpost_username', label: 'Devpost' },
  { key: 'status', label: 'Status' },
  { key: 'project_url', label: 'Project' },
  { key: 'created_at', label: 'Submitted', formatter: dateFieldFormatter },
  { key: 'actions', label: '', tdClass: 'text-nowrap' },
];

const pendingCount = computed(() => registrations.value.filter(r => r.status === 'pending').length);

const fetchData = async (): Promise<void> => {
  loading.value = true;
  error.value = false;
  try {
    registrations.value = await fetchRegistrations();
  } catch {
    error.value = true;
  } finally {
    loading.value = false;
  }
};

const processRegistration = async (id: number, action: Action): Promise<void> => {
  if (busy.value.has(id)) {
    return;
  }

  busy.value.set(id, action);
  try {
    const data = action === 'approve' ? await approveRegistration(id) : await rejectRegistration(id);
    const index = registrations.value.findIndex(r => r.id === id);
    if (index >= 0) {
      registrations.value[index] = { ...registrations.value[index], ...data };
    }
  } catch {
    error.value = true;
  } finally {
    busy.value.delete(id);
  }
};

onMounted(fetchData);
</script>

<template>
  <ThemedOverlay>
    <h1 class="mt-3">
      Transcend Hackathon registrations
    </h1>
    <p class="text-muted">
      {{ pendingCount }} pending - {{ registrations.length }} total
    </p>

    <ErrorState v-if="error" />

    <BTable
      v-else
      :items="registrations"
      :fields="fields"
      :busy="loading"
      small
      hover
      responsive
    >
      <template #cell(username)="{ value }">
        <BLink
          :to="{
            name: 'user',
            params: { userHandle: String(value) }}"
        >
          {{ value }}
        </BLink>
      </template>
      <template #cell(devpost_username)="{ value }">
        <a
          :href="`https://devpost.com/${value}`"
          target="_blank"
        >{{ value }}
        </a>
      </template>
      <template #cell(status)="{ value }">
        <BBadge :variant="statusVariant[String(value)] || 'secondary'">
          {{ value }}
        </BBadge>
      </template>
      <template #cell(project_url)="{ value }">
        <a
          v-if="value"
          :href="String(value)"
          target="_blank"
        >{{ value }}</a>
        <span
          v-else
          class="text-muted"
        >—</span>
      </template>
      <template #cell(actions)="{ item }">
        <template v-if="item.status === 'pending'">
          <BButton
            size="sm"
            variant="primary"
            class="me-1"
            :loading="busy.get(item.id) === 'approve'"
            loading-text="Approving..."
            @click="processRegistration(item.id, 'approve')"
          >
            Approve
          </BButton>
          <BButton
            size="sm"
            variant="outline-primary"
            :loading="busy.get(item.id) === 'reject'"
            loading-text="Rejecting..."
            @click="processRegistration(item.id, 'reject')"
          >
            Reject
          </BButton>
        </template>
      </template>
    </BTable>
  </ThemedOverlay>
</template>
